import cn from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import { RestartButton } from '@webapp/common/components/misc';
import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { useResetSpeechRecognCtx } from '@webapp/common/lib/speech-recogn-context';
import { SubstrateType } from '@webapp/common/lib/ui';
import { BundleType } from '@webapp/common/resources/entities';

import { QuestionsSection } from '@webapp/survey/src/components/questions';
import { substrateStyles } from '@webapp/survey/src/components/questions/lib';
import { TestResult } from '@webapp/survey/src/components/test-results';
import { isOfflineMode } from '@webapp/survey/src/machine/is-offline-mode';
import {
    selectCompletePageTextWithPromocodes,
    selectCurrentBundleIndex,
    selectCurrentVisibleBundle,
    selectInfo,
    selectIsDetailsLoading,
    selectIsQuotasLoading,
    selectIsStartPage,
    selectScrollToQuestionId,
    selectShowBundles,
    selectShowCompletePage,
    selectShowNav,
    selectShowWelcomePage,
    selectTestResult,
    selectWithClickReplyWithTwist,
} from '@webapp/survey/src/machine/selectors';
import { SurveyState } from '@webapp/survey/src/machine/states';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { useStore } from '@webapp/survey/src/store';
import { scrollSurveyToTop } from '@webapp/survey/src/utils/scroll';

import { BundlesForClickReply } from './bundles-for-click-reply';
import { SurveySection } from './container';
import { SurveyError } from './error';
import { SurveyNav } from './nav';
import { TextSection } from './text-section';

import css from './survey.css';

const BundleContent: VFC = () => {
    const { send } = useStore();
    const scrollToQuestionId = useStateSelector(selectScrollToQuestionId);
    const { questions, text, type } = useStateSelector(selectCurrentVisibleBundle);

    const resetScroll = useCallback(() => send({ type: 'SCROLL_RESET' }), [send]);

    switch (type) {
        case BundleType.QUESTIONS:
            return (
                <QuestionsSection
                    questions={questions}
                    resetScroll={resetScroll}
                    scrollToQuestionId={scrollToQuestionId}
                />
            );
        case BundleType.TEXT:
            return <TextSection text={text} />;
    }

    return null;
};
BundleContent.displayName = 'BundleContent';

export const SurveyContent: VFC<{
    marginStyle: CSSProperties;
}> = ({ marginStyle: marginStyleRaw }) => {
    const { state } = useStore();
    const broken = state === SurveyState.error;

    const showWelcomePage = useStateSelector(selectShowWelcomePage);
    const withClickReply = useStateSelector(selectWithClickReplyWithTwist);
    const completePageTextWithPromocodes = useStateSelector(selectCompletePageTextWithPromocodes);
    const showCompletePage = useStateSelector(selectShowCompletePage);
    const showBundles = useStateSelector(selectShowBundles);
    const isDetailsLoading = useStateSelector(selectIsDetailsLoading);
    const isQuotasLoading = useStateSelector(selectIsQuotasLoading);
    const showNav = useStateSelector(selectShowNav);
    const { design } = useStateSelector(selectInfo);
    const showTestResult = useStateSelector(selectTestResult);
    const currentBundleIndex = useStateSelector(selectCurrentBundleIndex);
    const isStartPage = useStateSelector(selectIsStartPage);
    const resetSpeechRecogn = useResetSpeechRecognCtx();

    useEffect(() => {
        return () => {
            resetSpeechRecogn();
        };
    }, [currentBundleIndex]);

    const offline = isOfflineMode();
    const { textStyle } = useTextStyle();

    let textSectionStyles: CSSProperties = textStyle;

    if (design.brand.substrate === SubstrateType.EVERY_QUESTION) {
        textSectionStyles = { ...textStyle, ...substrateStyles };
    }

    const marginStyle = useMemo(
        () => (withClickReply ? { ...marginStyleRaw, paddingTop: '0px', paddingBottom: '0px' } : marginStyleRaw),
        [marginStyleRaw, withClickReply],
    );
    const style = useMemo(
        () =>
            showWelcomePage
                ? {
                      paddingRight: marginStyle.paddingRight,
                      paddingLeft: marginStyle.paddingLeft,
                      paddingBottom: marginStyle.paddingBottom,
                  }
                : marginStyle,
        [marginStyle, showWelcomePage],
    );

    useEffect(() => {
        if (withClickReply) {
            document.body.classList.add('survey-with-click-reply');
        } else {
            document.body.classList.remove('survey-with-click-reply');
        }
    }, [withClickReply]);

    useEffect(() => {
        if (state === SurveyState.finished) {
            const timeoutId = setTimeout(() => {
                scrollSurveyToTop();
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [state]);

    if (isDetailsLoading) {
        return null;
    }

    return (
        <div
            style={style}
            className={cn(CssPageBlock.CONTENT, css.content, {
                'survey-with-click-reply': withClickReply && !isStartPage,
            })}
        >
            {isQuotasLoading && <Loader visible />}
            <SurveySection className={css.container}>
                {showTestResult && <TestResult />}
                {withClickReply ? (
                    <>
                        {showBundles && <BundlesForClickReply hide={showCompletePage} marginStyle={marginStyle} />}
                        {!showBundles && <SurveyNav />}
                    </>
                ) : (
                    <>
                        {showBundles && <BundleContent />}
                        {showNav && <SurveyNav />}
                    </>
                )}

                {showCompletePage && (
                    <div data-testid='complete-page' className={cn(withClickReply && css.finalSection)}>
                        <TextSection style={textSectionStyles} text={completePageTextWithPromocodes} />
                        {offline && <RestartButton />}
                    </div>
                )}
                {broken && <SurveyError />}
            </SurveySection>
        </div>
    );
};
SurveyContent.displayName = 'SurveyContent';
